import { KeyboardEvent, ReactElement, useEffect, useState } from 'react'
import styled from 'styled-components'
import { Autocomplete, Box, FormControl, TextField } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { AutoCheckImage } from '../../../../ui-components/AutoCheckImage'
import { TextInput } from '../../../../ui-components/forms/TextInput'
import { Checkbox } from '../../../../ui-components/forms/Checkbox'
import { Button } from '../../../../ui-components/forms/Button'
import {
  MutablePreference,
  useUserStore,
} from '../../../../store/user/UserStore'
import { I18n, Translate } from 'common'
import { defaultTheme } from '../../../../theme/index'
import { Spinner } from 'react-bootstrap'
import { vehicleWidgetStore } from '../store/VehicleWidgetStore'
import { usePartsCatalog } from '../../../../store/partsCatalog/PartsCatalog'
import { useSearchStore } from '../../../../store/search/SearchStore'
import { breakpoints } from '../../../../theme/breakpoints'
import { useUiStateStore } from 'src/store/uiState/UiStateStore'

export const LicensePlateSearch = observer((): ReactElement => {
  const navigate = useNavigate()
  const userStore = useUserStore()
  const statePreference = userStore.getStatePreference()
  const catalog = usePartsCatalog()
  const [plate, setPlate] = useState('')
  const [stateSelection, setStateSelection] = useState(statePreference)
  const { getStatesWithCountryId, states, statesLoading, currentVehicle } =
    useSearchStore()

  const stateList = states ?? []
  const [isLoading, setIsLoading] = useState(false)

  const { isAutoCheckEnabled } = useUiStateStore()

  const handleSearchByPlate = async () => {
    setIsLoading(true)
    const foundVehicle = await vehicleWidgetStore.lookupVehicle({
      licencePlate: `${stateSelection}:${plate}`,
    })
    setIsLoading(false)
    if (foundVehicle) {
      vehicleWidgetStore.setIsDrawerOpened(false)
      catalog.resetStore()
      navigate('/partsCatalog')
    }
  }

  const submitIfEnter = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSearchByPlate()
    }
  }

  useEffect(() => {
    getStatesWithCountryId()
  }, [getStatesWithCountryId])

  const isDefaultState = userStore.getStatePreference() === stateSelection

  return (
    <Wrapper>
      <MainRow>
        <Row>
          <Label>
            <I18n name="state" />
          </Label>
          <StyledFormControl sx={{ border: 0 }}>
            {statesLoading ? (
              <Box
                width="100%"
                height="100%"
                display="flex"
                justifyContent="center"
                alignItems="center"
              >
                <Spinner animation="border" />
              </Box>
            ) : (
              <StateWrapper>
                <Autocomplete
                  disablePortal
                  autoHighlight
                  id="state_selector_dropdown"
                  disableClearable
                  onChange={(_event: unknown, newValue: string | null) =>
                    setStateSelection(newValue)
                  }
                  value={stateSelection}
                  options={stateList.map((o) => o.regionCode)}
                  style={{ width: '100%', marginTop: '-5px' }}
                  renderInput={(params) => (
                    <TextField {...params} placeholder={Translate('State')} />
                  )}
                />
              </StateWrapper>
            )}
          </StyledFormControl>
          <MakeDefaultStateMobile>
            <Checkbox
              onClick={() => {
                userStore.updateUserPreferenceAndSyncToCloud(
                  MutablePreference.EPEFEATURES_DEFAULTSTATE,
                  stateSelection
                )
              }}
              value="makeDefault"
              active={isDefaultState}
              label={Translate('makeThisMyDefaultState')}
            />
          </MakeDefaultStateMobile>

          <Label>
            <I18n name="plateNumber" />
          </Label>
          <InputWrapper>
            <TextInput
              onChange={(text) => {
                setPlate(text)
              }}
              onKeyPress={submitIfEnter}
              borderRadius="0px 3px 3px 0px"
              value={plate}
              width="100%"
              autoFocus={true}
              placeholder="enterAPlateNumber"
              noMargin
            />
          </InputWrapper>

          <SyltedButton
            onClick={handleSearchByPlate}
            loading={isLoading}
            disabled={plate.length < 2}
            text="add"
            size="large"
          />
        </Row>

        {isAutoCheckEnabled() && (
          <AutoCheckImage vin={currentVehicle.vin} size="medium" />
        )}
      </MainRow>

      <MakeDefaultStateDesktop>
        <Checkbox
          onClick={() => {
            userStore.updateUserPreferenceAndSyncToCloud(
              MutablePreference.EPEFEATURES_DEFAULTSTATE,
              stateSelection
            )
          }}
          value="makeDefault"
          active={isDefaultState}
          label={Translate('makeThisMyDefaultState')}
          labelStyle={{
            fontSize: '11px',
            color: defaultTheme.colors.text,
          }}
        />
      </MakeDefaultStateDesktop>
    </Wrapper>
  )
})

const MainRow = styled.div`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
`

const MakeDefaultState = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  @media screen and (width < ${breakpoints.mobileLarge}px) {
    display: none;
  }
`

const MakeDefaultStateDesktop = styled(MakeDefaultState)`
  @media screen and (width < ${breakpoints.mobileLarge}px) {
    display: none;
  }
`

const MakeDefaultStateMobile = styled(MakeDefaultState)`
  display: none;
  margin-bottom: 24px;
  @media screen and (width < ${breakpoints.mobileLarge}px) {
    display: flex;
    justify-content: start;
    width: 100%;
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  flex-grow: 1;
`

const InputWrapper = styled.div`
  width: 233px;
  margin-right: 6px;

  @media screen and (width < ${breakpoints.mobileLarge}px) {
    width: 100%;
    margin-bottom: 24px;
    margin-right: 0px;
  }
`

const StyledFormControl = styled(FormControl)`
  border: 1px solid ${(p) => p.theme.colors.border};
  width: 97px;
  background-color: #fff;
  height: 50px;
  border-right: 0;
  border-radius: 3px 0px 0px 3px;
  .MuiOutlinedInput-notchedOutline {
    border: none !important;
  }
  text-align: left;
  display: flex;
  justify-content: space-between;
  padding: 0;
  @media screen and (width < ${breakpoints.mobileLarge}px) {
    width: 100%;
    border-right: 1px solid ${(p) => p.theme.colors.border};
    margin-bottom: 10px;
  }
`

const SyltedButton = styled(Button)`
  max-width: 128px;
  flex-grow: 1;
  @media screen and (width < ${breakpoints.mobileXLarge}px) {
    max-width: none;
    margin-right: 0px;
  }
`

const StateWrapper = styled.div`
  @media screen and (width < ${breakpoints.mobileXLarge}px) {
    width: 100%;
  }
`

const Label = styled.div`
  display: none;
  font-weight: 700;
  font-size: 14px;
  @media screen and (width < ${breakpoints.mobileLarge}px) {
    display: block;
    margin-bottom: 3px;
  }
`
